import { createContext, useContext, useEffect, useState } from "react";
import posthog from "posthog-js";
import { User } from "@/api/types";
import { getCurrentUser } from "@/api/auth";

interface AuthContextType {
  user: User | null;
  setUser: (user: User | null) => void;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("jwt");

    if (!token) {
      setIsLoading(false);
      return;
    }


    getCurrentUser()
      .then((user) => {
        setUser(user);
        posthog.identify(
          user.id.toString(),
          { email: user.email, coach_viewer: user.coach_viewer }
        );
      })
      .catch((error) => {
        console.error('Failed to get current user:', {
          error,
          message: error.message,
          status: error.status,
          name: error.name
        });

        // Only remove token if it's actually an auth error
        if (error.message?.toLowerCase().includes('unauthorized') ||
            error.status === 401) {
          console.error('Removing token due to auth error');
          localStorage.removeItem("jwt");
        }
        setUser(null);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  return (
    <AuthContext.Provider value={{ user, setUser, isLoading }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
